import React, { FC, useContext, useState } from 'react'
import Button from '../../common/Button/Button'
import FieldWrapper from '../../fields/FieldWrapper/FieldWrapper'
import Input from '../../fields/Input/Input'
import Phone from '../../fields/Phone/Phone'
import Textarea from '../../fields/Textarea/Textarea'
import ToggleCheckBox from '../../fields/ToggleCheckBox/ToggleCheckBox'
import { ButtonTheme } from '../../types'
import css from './WholeSaleForm.module.scss'
import { useForm, Controller } from 'react-hook-form'
import { isNotEmptyInput, requiredMessage } from '../../../lib/form'
import Sended from '../Sended/Sended'
import { useMutation } from '@apollo/client'
import { SEND_FORM } from '../../../queries/formMutation'
import { sendForm, sendFormVariables } from '../../../__generated__/sendForm'
import LoadingIcon from '../../../public/static/icons/form-loader.svg'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { LayoutContext } from '../../../contexts/layoutProvider'
import Captcha from '../Auth/Captcha'

interface IWholeSaleForm {
  activity: string
  organisation: string
  city: string
  links: string
  agent: string
  phone: string
  email: string
  comment: string
  privacyPolicy: boolean
}

const WholeSaleForm: FC<{ className?: string }> = ({ className }) => {
  const { register, handleSubmit, errors, control, reset, watch } =
    useForm<IWholeSaleForm>({
      mode: 'all',
    })
  const [sended, setSended] = useState(false)
  const { setCommonErrorMessage } = useContext(LayoutContext)
  const [sendForm, { loading }] = useMutation<sendForm, sendFormVariables>(
    SEND_FORM,
  )

  const { executeRecaptcha } = useGoogleReCaptcha()

  const submit = async (values) => {
    try {
      setCommonErrorMessage('')
      const token = await executeRecaptcha()

      const res = await sendForm({
        variables: {
          form: 'form-opts',
          name: `${values.agent} ${values.organisation}`,
          data: values,
          token,
        },
      })

      if (res?.data?.sendForm?.success) {
        setSended(true)

        setTimeout(() => {
          setSended(false)
          reset()
        }, 5000)
      } else {
        throw new Error(
          'Возникла ошибка при отправки формы. Воспользуйтесь формой позже.',
        )
      }
    } catch (error) {
      setCommonErrorMessage(
        'Возникла ошибка при отправки формы. Воспользуйтесь формой позже.',
      )
      console.error(error)
    }
  }

  const policy = watch('privacyPolicy')

  return (
    <div className={className}>
      {sended ? (
        <Sended
          heading="Анкета отправлена на рассмотрение"
          subheading=" С вами свяжутся в ближайшее время "
        />
      ) : (
        <form onSubmit={handleSubmit(submit)}>
          <h2 className={css.form_title}>Анкета покупателя</h2>
          <FieldWrapper
            label="Деятельность *"
            tooltip="Интернет-магазин, розничный магазин, студия красоты, совместные закупки, визажист и т.д."
            errorMessage={errors?.activity?.message}
          >
            <Input
              ref={register({
                required: requiredMessage,
                validate: (value) => isNotEmptyInput(value) || requiredMessage,
              })}
              required
              name="activity"
              id="activity"
              error={errors?.activity}
            />
          </FieldWrapper>

          <FieldWrapper label="Наименование организации/магазина">
            <Input name="organisation" id="organisation" ref={register} />
          </FieldWrapper>

          <h4 className={css.section_title}>Контактная информация</h4>

          <FieldWrapper label="Город *" errorMessage={errors?.city?.message}>
            <Input
              name="city"
              id="city"
              ref={register({
                required: requiredMessage,
                validate: (value) => isNotEmptyInput(value) || requiredMessage,
              })}
              required
              error={errors?.city}
            />
          </FieldWrapper>

          <FieldWrapper
            label="Ссылки на сайт/соц.сети или адрес магазина *"
            errorMessage={errors?.links?.message}
          >
            <Input
              name="links"
              id="links"
              ref={register({
                required: requiredMessage,
                validate: (value) => isNotEmptyInput(value) || requiredMessage,
              })}
              required
              error={errors?.links}
            />
          </FieldWrapper>

          <FieldWrapper
            label="Контактное лицо *"
            errorMessage={errors?.agent?.message}
          >
            <Input
              name="agent"
              id="agent"
              ref={register({
                required: requiredMessage,
                validate: (value) => isNotEmptyInput(value) || requiredMessage,
              })}
              required
              error={errors?.agent}
            />
          </FieldWrapper>

          <div className={css.line}>
            <FieldWrapper
              label="Телефон *"
              errorMessage={errors?.phone?.message}
            >
              <Controller
                name="phone"
                defaultValue=""
                rules={{
                  required: requiredMessage,
                  validate: (value) => {
                    return value.length === 17 || 'Неверный формат телефона'
                  },
                }}
                control={control}
                render={({ onChange, onBlur }) => (
                  <Phone
                    onChange={onChange}
                    onBlur={onBlur}
                    id="phone"
                    required
                    hasError={!!errors?.phone}
                  />
                )}
              />
            </FieldWrapper>

            <FieldWrapper
              label="E-mail *"
              errorMessage={errors?.email?.message}
            >
              <Input
                name="email"
                id="email"
                type="email"
                required
                ref={register({
                  required: requiredMessage,
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Некорректная почта',
                  },
                  validate: (value) =>
                    isNotEmptyInput(value) || requiredMessage,
                })}
                error={errors?.email}
              />
            </FieldWrapper>
          </div>

          <FieldWrapper label="Комментарий">
            <Textarea name="comment" id="comment" register={register()} />
          </FieldWrapper>

          <FieldWrapper errorMessage={errors?.privacyPolicy?.message}>
            <ToggleCheckBox
              name="privacyPolicy"
              id="privacyPolicy"
              label={
                <>
                  согласен{' '}
                  <a
                    href="/info/policy"
                    target="_blank"
                    className={css.policyLink}
                  >
                    на&nbsp;обработку персональных данных
                  </a>{' '}
                  и&nbsp;{' '}
                  <a
                    href="/info/personal-data-consent"
                    target="_blank"
                    className={css.policyLink}
                  >
                    даю согласие
                  </a>{' '}
                  на&nbsp;их&nbsp;использование
                </>
              }
              ref={register({
                validate: (value) => {
                  if (!value) {
                    return requiredMessage
                  }
                },
              })}
            />
          </FieldWrapper>

          {loading ? (
            <div className={css.loading}>
              <LoadingIcon />
            </div>
          ) : (
            <Button
              disabled={!policy}
              className={css.submit}
              type="submit"
              theme={ButtonTheme.black}
            >
              отправить заявку
            </Button>
          )}
        </form>
      )}
    </div>
  )
}

export default WholeSaleForm
